import { call, put, select, take, takeLatest } from 'redux-saga/effects';
import {
  ATTEMPT_SSO_LOGIN,
  ATTEMPT_MOBILE_SSO_LOGIN,
  ROUTE_SSO_FLOW,
  ROUTE_WEB_SSO_FLOW,
  ssoLoginActions,
  FINISH_MOBILE_SSO_LOGIN
} from '../reducer/sso-login-reducer';
import { saveLoginCookie } from '@rosetta/react-auth';
import { parse } from 'query-string';
import { appActions, LOG_LEVEL } from '../../app/reducer/app-reducer';
import {
  getAndClearMobileLoginSessionData,
  saveMobileLoginSessionData,
  getSamlEntityIdFromOrgId,
  getSkeletonOrgFromNamespace,
  installHappyUrl,
  validateAppId
} from '../services/sso-helpers';

import { push } from 'connected-react-router';
import { envConfigs } from '../../../envConfigs';
import { getRecommendations } from '../../launchpad/services/launchpad-api';
import {
  foundationsKey,
  gaiaKey,
  getProductInfoByKey,
  isParentProduct
} from '../../../helpers/productConfigHelper';
import { AUTH_INIT_COMPLETE, authActions, authSelectors } from '@rosetta/react-auth/dist/index';
import { getTargetLanguageCode, getUserInfo } from '../../app/util/user-helper';
import { emitEvent, SIGNED_IN } from '../../../helpers/eventingHelper';

// try logging in from the desktop sso login page
// all our facts are in the query params
function* attemptSsoLogin(action) {
  const queryParams = parse(action.payload.location.search);
  if (!queryParams.clientId) {
    return yield put(
      appActions.logEvent({
        level: LOG_LEVEL.INFO,
        message: 'came to ssologin with no clientId param'
      })
    );
  }
  if (!queryParams.salesPackageId) {
    return yield put(
      appActions.logEvent({
        level: LOG_LEVEL.INFO,
        message: 'came to ssologin with no salesPackageId param'
      })
    );
  }

  yield put(ssoLoginActions.setSsoLoginAttemptPending(true));

  try {
    const samlEntityId = yield call(getSamlEntityIdFromOrgId, queryParams.clientId);
    // redirect to idp via tully
    let redirectUrl = envConfigs.tullyApiHost + '/saml/login?idp=' + samlEntityId;
    redirectUrl += '&clientId=' + queryParams.clientId;
    if (queryParams.targetLanguage) {
      redirectUrl += '&targetLanguage=' + queryParams.targetLanguage;
    }
    redirectUrl += '&salesPackageId=' + queryParams.salesPackageId;
    appActions.logEvent({
      level: LOG_LEVEL.INFO,
      message: 'SAML SSO Redirect url : ' + redirectUrl
    });

    console.log('sso-login-sagas:72 location.href');
    window.location.href = redirectUrl;
  } catch (ex) {
    yield put(
      appActions.logEvent({
        level: LOG_LEVEL.ERROR,
        message: 'sso login failed because ' + ex.message
      })
    );
    return yield put(ssoLoginActions.setSsoLoginAttemptPending(false));
  }
}

/*
  this handles an authorized SSO login redirected from tully
 */
function* routeSsoFlow(action) {
  const queryParams = action.payload.queryParams;

  // peel off the mobile login here
  let mobileSSOLoginData = getAndClearMobileLoginSessionData();
  if (mobileSSOLoginData) {
    const loginStaleMilliseconds = 60000;
    const nowMs = Date.now();
    if (mobileSSOLoginData.time < nowMs + loginStaleMilliseconds) {
      // this is a mobileSSOlogin, go to that page and turn the authorized user
      // into a logged-in user
      mobileSSOLoginData.time = nowMs;
      mobileSSOLoginData.token = queryParams.rst_access_token;
      mobileSSOLoginData.userId = queryParams.userId;
      mobileSSOLoginData.email = queryParams.email;
      saveMobileLoginSessionData(mobileSSOLoginData);
      yield put(ssoLoginActions.setSsoLoginAttemptPending(true));
      return yield put(push('/mobilessologin'));
    }
  }

  // what follows is for non-mobile sso - it logs the user in
  //  by setting the loginCookie and then re-initing auth,
  //  sets their UI lang and heads off to the launchpad page
  const cookieData = {
    access_token: queryParams.rst_access_token,
    userId: queryParams.userId
  };
  yield call(saveLoginCookie, cookieData);
  yield put(
    authActions.initializeAuth({
      appId: 'launchpad',
      tullyHost: envConfigs.tullyApiHost,
      sirenHost: envConfigs.sirenApiHost,
      egoHost: envConfigs.egoApiHost
    })
  );
  // wait until all the user-checking is complete
  yield take(AUTH_INIT_COMPLETE);
  const userInterfaceLanguageCode = yield select(authSelectors.userInterfaceLanguage);
  yield put(appActions.setInterfaceLanguage(userInterfaceLanguageCode));
  yield call(emitEvent, SIGNED_IN, { 'Sign In Method': 'SSO' });
  yield put(push('/launchpad'));
}


/*
  this handles an authorized Web SSO login redirected from tully
 */
function* routeWebSsoFLow(action) {
  const queryParams = action.payload.queryParams;
  // what follows is for non-mobile (web) sso - it logs the user in
  //  by setting the loginCookie and then re-initing auth,
  //  sets their UI lang and heads off to the launchpad page
  const cookieData = {
    access_token: queryParams.rst_access_token,
    userId: queryParams.userId
  };
  yield call(saveLoginCookie, cookieData);
  yield put(
    authActions.initializeAuth({
      appId: 'launchpad',
      tullyHost: envConfigs.tullyApiHost,
      sirenHost: envConfigs.sirenApiHost,
      egoHost: envConfigs.egoApiHost
    })
  );
  // wait until all the user-checking is complete
  yield take(AUTH_INIT_COMPLETE);
  const userInterfaceLanguageCode = yield select(authSelectors.userInterfaceLanguage);
  yield put(appActions.setInterfaceLanguage(userInterfaceLanguageCode));
  yield call(emitEvent, SIGNED_IN, { 'Sign In Method': 'SSO' });
  yield put(push('/launchpad'));
}

// start the mobile sso login process
// NOTE: this is used by mobile and web
function* attemptMobileSsoLogin(action) {
  yield put(ssoLoginActions.setSsoLoginAttemptPending(true));

  const queryParams = action.payload.queryParams;
  let orgInfo = null;
  try {
    orgInfo = yield call(getSkeletonOrgFromNamespace, queryParams.sso_code);
  } catch (ex) {
    // handle errors below
  }
  if (orgInfo && orgInfo.samlEntityId) {
    // all good, we have the info we need now
    const loginData = {
      time: Date.now(),
      sso_code: queryParams.sso_code,
      org_guid: orgInfo.globalUniqueId,
      ui_language: queryParams.ui_language,
      app_id: queryParams.app_id,
      api_version: queryParams.api_version
    };
    console.log('sso-login-sagae: api_version:', queryParams.api_version);
    yield call(saveMobileLoginSessionData, loginData);
    // redirect to tully
    const redirectUrl =
      envConfigs.tullyApiHost + '/saml/login?idp=' + orgInfo.samlEntityId + '&clientId=' + orgInfo.globalUniqueId;
    console.log('sso-login-sagas:191 location.href');
    window.location.href = redirectUrl;
  } else if (orgInfo === null || (orgInfo && typeof orgInfo.status !== 'undefined' && orgInfo.status === 0)) {
    // network fail error
    yield put(ssoLoginActions.setMobileSsoLoginError('_mobile_sso_network_error'));
    yield put(ssoLoginActions.setSsoLoginAttemptPending(false));
  } else {
    // missing data so show "bad sso code" error
    yield put(ssoLoginActions.setMobileSsoLoginError('_mobile_sso_company_code_invalid'));
    yield put(ssoLoginActions.setSsoLoginAttemptPending(false));
  }
}

export function* displaySsoError(errorKey, uiLangCode) {
  // 'export' for UTs
  yield put(push('/ssoerror/' + errorKey + '?ui_language=' + uiLangCode));
}

/**
 * finishMobileSsoLogin() is for when we get a mobile-sso-login-success response back from tully
 *  and we have to make sure the user is ready to launch in the mobile app.
 *  Possible error cases are:
 *    * they have not onboarded
 *    * they launched the wrong app
 *  Onboarding requires that they log in web browser (mobile or desktop), which will give them a
 *    license as well as create them as an RS user.  Then they have to complete the questionnaire
 *    and in some cases a placement test.  The test we have here is to call LPS.requirements and
 *    see if the non-optional requirement we get back is for the app which they logged in from.
 *
 *  In addition, there's the case where the user has optional requirements - upcoming tests -
 *    it's not an error but we have to notify them
 */
export function* finishMobileSsoLogin(action) {
  // 'export' for UTs
  yield put(ssoLoginActions.setSsoLoginAttemptPending(true));

  let mobileSSOLoginData = action.payload.mobileSSOLoginData;
  // in mobile login, we may get the UI language from either the initial login params
  //  or, if we successfully log in the user, from the user's data (which overrides)
  let mobileUILanguage = mobileSSOLoginData.ui_language;

  let userInfo = null;
  try {
    userInfo = yield call(getUserInfo, mobileSSOLoginData.userId, mobileSSOLoginData.token);
  } catch (ex) {
    return yield call(displaySsoError, ex.message, mobileUILanguage);
  }
  if (userInfo.interfaceLanguage) {
    mobileUILanguage = userInfo.interfaceLanguage;
  }
  if (!getTargetLanguageCode(userInfo)) {
    // user has not been assigned or chosen a targetLanguage, that's an error here
    return yield call(displaySsoError, '_mobile_sso_error_not_onboarded', mobileUILanguage);
  }
  const foundationsId = getProductInfoByKey(foundationsKey).sirenProductId;
  const gaiaId = getProductInfoByKey(gaiaKey).sirenProductId;
  const webiEnglishId = 'product.8c3d3ec6-aa91-e298-0838-c87f9c63492d';
  const speakHiId = 'product.a7d387bb-79c6-771a-9ea3-9e4025133680';

  // get user recommendations from LaunchpadService
  let recommendations = null;
  try {
    recommendations = yield call(
      getRecommendations,
      userInfo.firstName + ' ' + userInfo.lastName,
      mobileUILanguage,
      userInfo.targetLanguage.language,
      mobileSSOLoginData.token
    );
  } catch (ex) {
    return yield call(displaySsoError, ex.message, mobileUILanguage);
  }

  // figure out whether there's errors or not:
  let errorCode;
  for (let i = 0; i < recommendations.length; i++) {
    const recommendation = recommendations[i];
    // REFACTOR: This is a fake error added to open a modal, it isn't a legitimate failure
    if (recommendation.optional || recommendation.warning) {
      // if they have a hard error don't replace it with this notification
      if (!errorCode) {
        // handle case where user should be notified of upcoming test
        mobileSSOLoginData.days_until_trigger = recommendation.days_until_trigger;
        errorCode = 'TEST_NOTIFICATION';
      }
    } else {
      if (isParentProduct(recommendation.product_id)) {
        if (!recommendation.sub_product_id) {
          errorCode = '_mobile_sso_error_not_onboarded';
        } else if (validateAppId(mobileSSOLoginData.app_id, recommendation.sub_product_id)) {
          // all good, do the success thing
          installHappyUrl(mobileSSOLoginData);
        } else {
          // catalyst but the wrong subproduct, baaaaad
          errorCode = '_mobile_sso_error_wrong_app';
          mobileSSOLoginData.product_name = recommendation.product_name;
        }
      } else if (validateAppId(mobileSSOLoginData.app_id, recommendation.product_id)) {
        // this is success for standalone (non-catalyst)
        installHappyUrl(mobileSSOLoginData);
      } else if (
        recommendation.product_id === gaiaId ||
        recommendation.product_id === foundationsId ||
        recommendations.product_id === webiEnglishId ||
        recommendations.product_id === speakHiId
      ) {
        // not sure how this would happen but they have a recommendation for a different app
        //  when they logged in standalone
        errorCode = '_mobile_sso_error_wrong_app';
        mobileSSOLoginData.product_name = recommendation.product_name;
      }
    }
  }

  if (!errorCode && !mobileSSOLoginData.happyUrl) {
    // no recommendations fit our expectations, it is an unknown error
    errorCode = 'sso_error_msg_default';
  }

  if (errorCode) {
    // store all the info we might need when handling errors
    saveMobileLoginSessionData(mobileSSOLoginData);
    return yield call(displaySsoError, errorCode, mobileUILanguage);
  } else {
    window.location.assign(mobileSSOLoginData.happyUrl);
  }
}

export function* ssoLoginSagas() {
  yield takeLatest(ATTEMPT_SSO_LOGIN, attemptSsoLogin);
  yield takeLatest(ROUTE_SSO_FLOW, routeSsoFlow);
  yield takeLatest(ROUTE_WEB_SSO_FLOW, routeWebSsoFLow);
  yield takeLatest(ATTEMPT_MOBILE_SSO_LOGIN, attemptMobileSsoLogin);
  yield takeLatest(FINISH_MOBILE_SSO_LOGIN, finishMobileSsoLogin);
}



// WEBPACK FOOTER //
// ./src/modules/login/sagas/sso-login-sagas.js